import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { message } from 'antd';
import { apiService } from '../../services/api';
import LteBandSection from '../lte-band-section';
import '../port-create-panel/port-create-panel.css';

/**
 * PortBulkEditPanel — slide-in панель массового редактирования модемов.
 * Повторяет дизайн PortEditPanel (pcp-* классы).
 * Все поля необязательны — пустые поля не будут изменены.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = ({ title }) => (
  <svg className="pcp__info-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label={title}>
    <title>{title}</title>
    <circle cx="6" cy="6" r="5.25" stroke="currentColor" strokeWidth="1" />
    <path d="M6 5.5V8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <circle cx="6" cy="3.75" r="0.6" fill="currentColor" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 7.5L6 5L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SelectChevron = () => (
  <svg className="pcp__select-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== SELECT OPTIONS ====================

const NET_MODE_OPTIONS = [
  { value: 'lte', label: 'LTE (4G)' },
  { value: '5g', label: '5G' },
  { value: 'umts', label: 'UMTS (3G)' },
  { value: 'auto', label: 'Авто' },
];

const RECONNECT_TYPE_OPTIONS = [
  { value: 'time', label: 'По времени' },
  { value: 'link', label: 'Ссылка' },
  { value: 'time_link', label: 'Время + ссылка' },
];

/** Условия переподключения модема в Kraken API (поле reconnect_event) — как в PortEditPanel */
const KRAKEN_RECONNECT_EVENT_UI = [
  { token: 'not_provider', label: 'Нет подключения к оператору' },
  { token: 'not_inet', label: 'Нет подключения к интернету' },
  { token: 'unique_ip', label: 'Не уникальный внешний IP' },
];

const UI_RECONNECT_EVENT_TOKENS = KRAKEN_RECONNECT_EVENT_UI.map((x) => x.token);

const normalizeReconnectEventList = (raw) => {
  const arr = Array.isArray(raw) ? [...raw] : [];
  const rest = arr.filter((t) => !UI_RECONNECT_EVENT_TOKENS.includes(t));
  const picked = UI_RECONNECT_EVENT_TOKENS.filter((t) => arr.includes(t));
  return [...rest, ...picked];
};

const mergeExtrasIntersection = (lists) => {
  if (!lists.length) return [];
  const extrasPer = lists.map((l) => {
    const n = normalizeReconnectEventList(l);
    return n.filter((t) => !UI_RECONNECT_EVENT_TOKENS.includes(t));
  });
  return extrasPer[0].filter((t) => extrasPer.every((ex) => ex.includes(t)));
};

// ==================== SUB-COMPONENTS ====================

const NumberField = ({ label, value, onChange, onKeyDown, min, max, step = 1, infoTitle, placeholder }) => {
  const handleIncrement = () => {
    const num = Number(value) || 0;
    const next = Math.min(num + step, max ?? Infinity);
    onChange(next);
  };

  const handleDecrement = () => {
    const num = Number(value) || 0;
    const next = Math.max(num - step, min ?? -Infinity);
    onChange(next);
  };

  return (
    <div className="pcp__field">
      <div className="pcp__label">
        <span className="pcp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className="pcp__number-wrap">
        <input
          type="number"
          className="pcp__input pcp__input--number"
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { onChange(undefined); return; }
            let num = Number(raw);
            if (isNaN(num)) return;
            if (min !== undefined && num < min) num = min;
            if (max !== undefined && num > max) num = max;
            onChange(num);
          }}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder || 'Не изменять'}
          aria-label={label}
        />
        <div className="pcp__number-arrows">
          <button type="button" className="pcp__number-arrow pcp__number-arrow--up" onClick={handleIncrement} tabIndex={-1} aria-label="Увеличить">
            <ChevronUpIcon />
          </button>
          <button type="button" className="pcp__number-arrow pcp__number-arrow--down" onClick={handleDecrement} tabIndex={-1} aria-label="Уменьшить">
            <ChevronDownIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange, indeterminate, disabled = false }) => {
  const handleToggleClick = () => {
    if (disabled) return;
    // Смешанное состояние: первый клик выключает условие на всех выбранных модемах (единое «выкл»)
    if (indeterminate) {
      onChange(false);
      return;
    }
    onChange(!checked);
  };

  const ariaLabel = indeterminate
    ? `${label}. Смешанно: у модемов разные значения. Выключить для всех выбранных`
    : label;

  return (
    <div className={`pcp__toggle-row${disabled ? ' pcp__toggle-row--disabled' : ''}`}>
      <span className="pcp__toggle-label">{label}</span>
      <button
        type="button"
        className={`pcp__toggle${checked ? ' pcp__toggle--on' : ''}${indeterminate ? ' pcp__toggle--indeterminate' : ''}`}
        onClick={handleToggleClick}
        disabled={disabled}
        role="switch"
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
      >
        <span className="pcp__toggle-dot" />
      </button>
    </div>
  );
};

const SelectField = ({
  label,
  placeholder,
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  searchable = true,
  infoTitle,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && searchable && searchRef.current) searchRef.current.focus();
  }, [open, searchable]);

  const selectedOption = options.find(o => o.value === value);
  const displayText = selectedOption ? selectedOption.label : null;
  const filtered = search ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())) : options;

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(undefined);
    setOpen(false);
    setSearch('');
  };

  const triggerClasses = [
    'pcp__select-trigger',
    !displayText ? 'pcp__select-trigger--placeholder' : '',
    disabled ? 'pcp__select-trigger--disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="pcp__field">
      <div className="pcp__label">
        <span className="pcp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className={`pcp__select-wrap${open ? ' pcp__select-wrap--open' : ''}`} ref={wrapRef}>
        <button
          type="button"
          className={triggerClasses}
          onClick={() => { if (!disabled) setOpen(!open); }}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        >
          <span className="pcp__select-value">
            {displayText || placeholder || 'Не изменять'}
          </span>
          {displayText ? (
            <span className="pcp__select-clear" onClick={handleClear} role="button" tabIndex={0} aria-label="Очистить">✕</span>
          ) : (
            <SelectChevron />
          )}
        </button>

        {open && (
          <div className="pcp__select-dropdown" role="listbox">
            {searchable && (
              <input
                ref={searchRef}
                type="text"
                className="pcp__select-search"
                placeholder="Поиск..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {loading && <div className="pcp__select-empty">Загрузка...</div>}
            {!loading && filtered.length === 0 && <div className="pcp__select-empty">Ничего не найдено</div>}
            {!loading && filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`pcp__select-option${opt.value === value ? ' pcp__select-option--active' : ''}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== HELPERS ====================

const bufferToAPI = (v) => {
  const n = Number(v);
  if (isNaN(n)) return undefined;
  return n > 1 ? n / 100 : n;
};

// ==================== MAIN COMPONENT ====================

const PortBulkEditPanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  onBandApplied = null,
  submitting = false,
  selectedCount = 0,
  selectedPorts = [],
  modemTypes = [],
  tcpFingerprints = [],
  loadingTcpFingerprints = false,
}) => {
  const [formData, setFormData] = useState({});
  const [reconnectIndeterminate, setReconnectIndeterminate] = useState({});
  const [krakenLiveLoading, setKrakenLiveLoading] = useState(false);
  const [krakenLiveError, setKrakenLiveError] = useState(null);
  const [krakenLiveOk, setKrakenLiveOk] = useState(false);
  const reconnectBaselineReadyRef = useRef(false);
  const reconnectInitialSerializedRef = useRef('');
  const liveFetchGenRef = useRef(0);
  /** Пользователь менял reconnect_event после загрузки — не затирать при позднем setState */
  const reconnectUserEditedRef = useRef(false);
  /** Любое изменение полей — для текста, если payload пустой */
  const formDirtyRef = useRef(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const selectionKey = useMemo(() => {
    if (!selectedPorts?.length) return '';
    return [...selectedPorts]
      .map((p) => String(p.server_id ?? p.id ?? ''))
      .sort()
      .join(',');
  }, [selectedPorts]);

  useEffect(() => {
    if (visible) {
      formDirtyRef.current = false;
      setFormData({});
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      reconnectBaselineReadyRef.current = false;
      setReconnectIndeterminate({});
      setKrakenLiveLoading(false);
      setKrakenLiveError(null);
      setKrakenLiveOk(false);
      return;
    }
    if (!selectionKey || !selectedPorts?.length) {
      reconnectBaselineReadyRef.current = false;
      return;
    }

    const gen = liveFetchGenRef.current + 1;
    liveFetchGenRef.current = gen;
    setKrakenLiveLoading(true);
    setKrakenLiveError(null);
    setKrakenLiveOk(false);
    setReconnectIndeterminate({});
    reconnectBaselineReadyRef.current = false;
    reconnectUserEditedRef.current = false;
    formDirtyRef.current = false;

    const run = async () => {
      const uniqueServerIds = [...new Set(
        selectedPorts.map((p) => p.server_id || p.id).filter(Boolean),
      )];

      const liveByServer = new Map();
      if (uniqueServerIds.length > 0) {
        await Promise.all(uniqueServerIds.map(async (sid) => {
          try {
            const res = await apiService.getKrakenPortLiveModem(sid);
            const raw = res?.data?.reconnect_event;
            const normalized = normalizeReconnectEventList(Array.isArray(raw) ? raw : []);
            liveByServer.set(sid, { ok: true, list: normalized });
          } catch {
            liveByServer.set(sid, { ok: false, list: null });
          }
        }));
      }

      if (gen !== liveFetchGenRef.current) return;

      const perPortLists = selectedPorts.map((port) => {
        const sid = port.server_id || port.id;
        const entry = sid ? liveByServer.get(sid) : null;
        if (entry?.ok && Array.isArray(entry.list)) {
          return entry.list;
        }
        return normalizeReconnectEventList(port.reconnect_event);
      });

      const n = perPortLists.length;
      const indeter = {};
      const unanimousOn = [];
      for (const token of UI_RECONNECT_EVENT_TOKENS) {
        const hasCount = perPortLists.filter((l) => l.includes(token)).length;
        if (n > 0 && hasCount === n) unanimousOn.push(token);
        else if (hasCount > 0 && hasCount < n) indeter[token] = true;
      }

      const extrasIntersection = mergeExtrasIntersection(perPortLists);
      const merged = normalizeReconnectEventList([...extrasIntersection, ...unanimousOn]);

      reconnectInitialSerializedRef.current = JSON.stringify(merged);
      reconnectBaselineReadyRef.current = true;
      setFormData((prev) => {
        if (reconnectUserEditedRef.current) {
          return prev;
        }
        const next = { ...prev, reconnect_event: merged };
        formDataRef.current = next;
        return next;
      });
      setReconnectIndeterminate(indeter);

      const anyOk = uniqueServerIds.some((sid) => liveByServer.get(sid)?.ok);
      const anyFail = uniqueServerIds.some((sid) => {
        const e = liveByServer.get(sid);
        return e && e.ok === false;
      });
      if (uniqueServerIds.length === 0) {
        setKrakenLiveOk(false);
        setKrakenLiveError('Нет server_id — синхронизация с Kraken недоступна');
      } else if (!anyOk) {
        setKrakenLiveOk(false);
        setKrakenLiveError('Не удалось получить данные Kraken (GET modem) для выбранных модемов');
      } else if (anyFail) {
        setKrakenLiveOk(false);
        setKrakenLiveError('Часть модемов: значения из списка портов, Kraken недоступен');
      } else {
        setKrakenLiveOk(true);
        setKrakenLiveError(null);
      }

      setKrakenLiveLoading(false);
    };

    run();
  }, [visible, selectionKey, selectedPorts]);

  const handleChange = useCallback((field, value) => {
    formDirtyRef.current = true;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      formDataRef.current = next;
      return next;
    });
  }, []);

  const handleReconnectEventTokenChange = useCallback((token, enabled) => {
    reconnectUserEditedRef.current = true;
    formDirtyRef.current = true;
    setReconnectIndeterminate((prev) => ({ ...prev, [token]: false }));
    setFormData((prev) => {
      const cur = new Set(prev.reconnect_event || []);
      if (enabled) cur.add(token);
      else cur.delete(token);
      const next = { ...prev, reconnect_event: normalizeReconnectEventList([...cur]) };
      formDataRef.current = next;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (!onSubmit) return;

    const fd = formDataRef.current;
    const result = {};

    if (fd.type !== undefined && fd.type !== null) result.type = Number(fd.type);
    if (fd.net_mode !== undefined) result.net_mode = fd.net_mode;
    if (fd.mtu !== undefined) result.mtu = fd.mtu;
    if (fd.capacity_speed_mbps !== undefined) result.capacity_speed_mbps = Number(fd.capacity_speed_mbps);
    if (fd.max_tcp_connections !== undefined) result.max_tcp_connections = parseInt(fd.max_tcp_connections, 10);
    if (fd.safety_buffer !== undefined) result.safety_buffer = bufferToAPI(fd.safety_buffer);
    if (fd.monitoring_interval !== undefined) result.monitoring_interval = fd.monitoring_interval;
    if (fd.reconnect_type !== undefined) result.reconnect_type = fd.reconnect_type;
    if (fd.reconnect_interval !== undefined) result.reconnect_interval = fd.reconnect_interval;
    if (fd.reconnect_min !== undefined) result.reconnect_min = fd.reconnect_min;
    if (fd.is_osfp !== undefined) {
      result.is_osfp = fd.is_osfp;
      result.osfp = fd.is_osfp ? (fd.osfp || null) : null;
    }
    if (fd.osfp !== undefined && fd.is_osfp) result.osfp = fd.osfp;

    const curSerialized = JSON.stringify(normalizeReconnectEventList(fd.reconnect_event || []));
    // После правки переключателей из «смешанного» UI-baseline может совпасть с итогом, но на модемах значения разные — всегда шлём список
    if (reconnectUserEditedRef.current) {
      result.reconnect_event = normalizeReconnectEventList(fd.reconnect_event || []);
    } else if (reconnectBaselineReadyRef.current && curSerialized !== reconnectInitialSerializedRef.current) {
      result.reconnect_event = normalizeReconnectEventList(fd.reconnect_event || []);
    }

    if (Object.keys(result).length === 0) {
      if (!formDirtyRef.current) {
        message.info('Нет изменений для применения: измените поле или переключатель настроек модема.');
      } else if (reconnectUserEditedRef.current && curSerialized === reconnectInitialSerializedRef.current) {
        message.info(
          'Переключатели условий модема приведены к одному виду для всех выбранных — измените другое поле или сохраните снова после правки списка условий.',
        );
      } else {
        message.info('Нет отличий от текущих значений: измените поля настроек модема.');
      }
      return;
    }

    onSubmit(result);
  }, [onSubmit]);

  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); }
  }, [handleSubmit]);

  // ==================== DERIVED OPTIONS ====================

  const modemTypeOptions = modemTypes.length > 0
    ? modemTypes.map(t => ({ value: t.id, label: t.name || `Type ${t.id}` }))
    : [
        { value: 1, label: 'Huawei E3372h-153' },
        { value: 4, label: 'Huawei E3372h-320' },
        { value: 6, label: 'Huawei E8372h-320' },
      ];

  const fingerprintOptions = tcpFingerprints.map(fp => ({
    value: fp.id,
    label: fp.name ? `${fp.name}${fp.modem ? ` (${fp.modem})` : ''}` : `ID: ${fp.id}`,
  }));

  if (!visible) return null;

  return createPortal(
    <div className="pcp-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="pcp" role="dialog" aria-label="Массовое редактирование модемов">

        {/* ==================== HEADER ==================== */}
        <div className="pcp__header">
          <h2 className="pcp__title">массовое редактирование ({selectedCount})</h2>
          <button type="button" className="pcp__close-btn" onClick={handleClose} aria-label="Закрыть" tabIndex={0}>
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="pcp__body">

          {/* --- Информационная плашка --- */}
          <div className="pcp__alert">
            Оставьте поля пустыми, если не хотите их изменять. Изменения будут применены к {selectedCount} выбранным модемам.
          </div>

          {/* --- Section: Основные настройки модема --- */}
          <div className="pcp__section">
            <div className="pcp__section-title">Основные настройки модема</div>

            <div className="pcp__row">
              <SelectField
                label="Тип модема"
                placeholder="Не изменять"
                value={formData.type}
                onChange={(v) => handleChange('type', v)}
                options={modemTypeOptions}
                loading={modemTypes.length === 0}
              />
              <SelectField
                label="Тип сети"
                placeholder="Не изменять"
                value={formData.net_mode}
                onChange={(v) => handleChange('net_mode', v)}
                options={NET_MODE_OPTIONS}
                searchable={false}
              />
            </div>

            <div className="pcp__row">
              <NumberField
                label="MTU (0 — по умолчанию)"
                value={formData.mtu}
                onChange={(v) => handleChange('mtu', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={9000}
              />
            </div>
          </div>

          {/* --- Section: Характеристики производительности --- */}
          <div className="pcp__section">
            <div className="pcp__section-title">Характеристики производительности</div>

            <div className="pcp__row">
              <NumberField
                label="Скорость (Мб/с)"
                value={formData.capacity_speed_mbps}
                onChange={(v) => handleChange('capacity_speed_mbps', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={1000}
                infoTitle="Максимальная пропускная способность порта в Мб/с"
              />
              <NumberField
                label="Макс. TCP соединений"
                value={formData.max_tcp_connections}
                onChange={(v) => handleChange('max_tcp_connections', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={10000}
                infoTitle="Максимальное количество одновременных TCP соединений"
              />
            </div>

            <div className="pcp__row">
              <NumberField
                label="Буфер безопасности (%)"
                value={formData.safety_buffer}
                onChange={(v) => handleChange('safety_buffer', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={100}
              />
            </div>
          </div>

          {/* --- Section: Настройки мониторинга --- */}
          <div className="pcp__section pcp__section--grow">
            <div className="pcp__section-title">Настройки мониторинга</div>

            <div className="pcp__row">
              <NumberField
                label="Интервал мониторинга (сек)"
                value={formData.monitoring_interval}
                onChange={(v) => handleChange('monitoring_interval', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={300}
                infoTitle="Интервал опроса статуса модема в секундах"
              />
              <SelectField
                label="Тип переподключения"
                placeholder="Не изменять"
                value={formData.reconnect_type}
                onChange={(v) => handleChange('reconnect_type', v)}
                options={RECONNECT_TYPE_OPTIONS}
                searchable={false}
              />
            </div>

            <div className="pcp__row">
              <NumberField
                label="Интервал переподключения (сек)"
                value={formData.reconnect_interval}
                onChange={(v) => handleChange('reconnect_interval', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={3600}
              />
              <NumberField
                label="Минимальный шаг (сек)"
                value={formData.reconnect_min}
                onChange={(v) => handleChange('reconnect_min', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={300}
              />
            </div>

            {/* Toggle: Подмена TCP отпечатка */}
            <ToggleRow
              label="Использовать подмену TCP отпечатка"
              checked={formData.is_osfp ?? false}
              onChange={(v) => {
                handleChange('is_osfp', v);
                if (!v) handleChange('osfp', undefined);
              }}
            />

            {formData.is_osfp && (
              <div className="pcp__row">
                <SelectField
                  label="TCP отпечаток"
                  placeholder="Выберите TCP отпечаток"
                  value={formData.osfp}
                  onChange={(v) => handleChange('osfp', v)}
                  options={fingerprintOptions}
                  loading={loadingTcpFingerprints}
                />
              </div>
            )}

            <div className="pcp__subsection">
              <div className="pcp__subsection-title">Условия переподключения модема (Kraken)</div>
              {krakenLiveLoading && (
                <p className="pcp__subsection-status" role="status">
                  Загрузка значений из Kraken…
                </p>
              )}
              {!krakenLiveLoading && krakenLiveOk && !krakenLiveError && (
                <p className="pcp__subsection-status pcp__subsection-status--ok" role="status">
                  Переключатели соответствуют данным Kraken (GET modem).
                </p>
              )}
              {!krakenLiveLoading && krakenLiveError && (
                <p className="pcp__subsection-status pcp__subsection-status--err" role="alert">
                  {krakenLiveError}
                </p>
              )}
              {KRAKEN_RECONNECT_EVENT_UI.map(({ token, label }) => (
                <ToggleRow
                  key={token}
                  label={label}
                  checked={Boolean((formData.reconnect_event || []).includes(token))}
                  indeterminate={Boolean(reconnectIndeterminate[token])}
                  onChange={(v) => handleReconnectEventTokenChange(token, v)}
                  disabled={krakenLiveLoading}
                />
              ))}
            </div>
          </div>

          <LteBandSection
            visible={visible}
            bulk
            modemIds={(selectedPorts || []).map((p) => p.server_id ?? p.id).filter(Boolean)}
            onApplied={onBandApplied}
          />
        </div>

        {/* ==================== FOOTER ==================== */}
        <div
          className="pcp__footer"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="pcp__btn pcp__btn--cancel" onClick={handleClose} disabled={submitting} aria-label="Отмена" tabIndex={0}>
            Отмена
          </button>
          <button
            type="button"
            className="pcp__btn pcp__btn--submit"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit();
            }}
            disabled={submitting}
            aria-label="Применить"
            tabIndex={0}
          >
            {submitting ? 'Применение...' : `Применить к ${selectedCount} модемам`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PortBulkEditPanel;
